import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from "@/api/constant";
import Breadcrumb from "@/components/common/Breadcrumb";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import ImagePreview from "@/components/common/media/ImagePreview";
import Layout from "@/components/Layout/Layout";
import Card from "@/components/UI/Card";
import DetailsCard from "@/components/UI/DetailsCard";
import { usePermissions } from "@/hooks/usePermissions";
import { ActionConfig } from "@/interfaces/common.interface";
import { IRatingDetail, Product } from "@/interfaces/ratingDetail.interface";
import { checkIsExpire, formatDateTime, toFixedThree } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface StatesProp {
    ratingDetail: IRatingDetail | null
}
const ReviewRatingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canView } = usePermissions();

    const breadcrumb = [
        { label: 'Reviews & Ratting', href: RoutesLink.reviewRatingDetails },
        { label: 'Details' },
    ];
    const [states, setStates] = useState<StatesProp>({
        ratingDetail: null
    })

    const handleChangeState = (newState: Partial<StatesProp>) => {
        setStates((prev) => ({
            ...prev,
            ...newState,
        }));
    };

    const fetchRatingDetail = async () => {
        try {
            const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.rating}/${id}`;
            const res = await callApi<IRatingDetail>(PATH, { method: 'GET' });
            handleChangeState({ ratingDetail: res })
        } catch (error) {
            ThrowError(error)
        }
    };

    useEffect(() => {
        if (id) fetchRatingDetail()
    }, [id]);


    const reviewDetail = [
        {
            label: 'Review',
            value: states?.ratingDetail?.review
        },
        {
            label: 'Ratting',
            value: `${toFixedThree(states?.ratingDetail?.rating || 0, 1)}`,
        },
        {
            label: 'Review Status',
            value: states?.ratingDetail?.status
        },
        {
            label: 'Uploaded At',
            value: `${formatDateTime(states?.ratingDetail?.createdAt, 'YYMMMDDHH')}`
        },
    ];

    const customerDetail = [
        {
            label: 'Name',
            value: states?.ratingDetail?.user?.fullName
        },
        {
            label: 'Email',
            value: `${states?.ratingDetail?.user?.email}`,
        },
        {
            label: 'Phone No.',
            value: `${states?.ratingDetail?.user?.countryCode} ${states?.ratingDetail?.user?.phone}`
        },
        {
            label: 'Verification Status',
            value: `${states?.ratingDetail?.user?.isVerified ? 'Verified' : 'Unverified'}`
        },
    ];

    const productActionConfig: Record<string, ActionConfig<Product>> = {
        view: {
            text: 'View',
            onClick: (product) =>
                navigate(`${RoutesLink.productDetail}/${product.id}`),
            permission: () => canView('product'),
        },
    }

    const actions = useMemo(
        () =>
            Object.values(productActionConfig)
                .filter((action) => action.permission())
                .map(({ permission, ...rest }) => rest),
        [canView],
    );


    const productsColumn: TableColumn<Product>[] = [
        {
            key: 'image',
            header: 'Image',
            render: (item) => {
                const url = item?.directImageUrl
                    ? item?.directImageUrl
                    : item?.media?.[0]?.url;
                return (
                    <ImagePreview
                        src={`${API_URL_PRODUCT_SERVICE}/${url}`}
                        alt=""
                        text={item?.name}
                    />
                );
            },
        },
        {
            key: 'price',
            header: 'Price',
            render: (item) => toFixedThree(item.productInfo[0]?.secondaryPrice || item.productInfo[0]?.price || 0),
        },
        {
            key: 'stock',
            header: 'Stock',
            render: (item) => item.productInfo[0]?.quantity || 0,
        },
        {
            key: 'discount',
            header: 'Discount',
            render: (item) => (
                <span>
                    {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
                </span>
            ),
        },
        {
            key: 'ItemCode',
            header: 'Item Code',
            render: (item) => (
                <span>
                    {item?.itemCode || '--'}
                </span>
            ),
        },
        {
            key: 'ItemId',
            header: 'Item Id',
            render: (item) => (
                <span>
                    {item?.inventoryItemId}
                </span>
            ),
        },
    ];

    return (
        <Layout>
            <Breadcrumb items={breadcrumb} />
            <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
                <DetailsCard title="Review Information" details={reviewDetail} />
                <DetailsCard title="Customer Information" details={customerDetail} />
            </div>
            <div className="mt-6">
                {
                    states?.ratingDetail?.product && (
                        <Card>
                            <CommonTable
                                columns={productsColumn}
                                data={[states?.ratingDetail?.product]}
                                minHeight="44"
                                actions={actions}
                            />
                        </Card>
                    )
                }
            </div>
        </Layout >
    )
}

export default ReviewRatingDetail;